import React from 'react';
import { LayoutDashboard, FileText, BarChart2, LogOut, Users, Megaphone, LineChart, Trophy, MessageSquare, Shield, Bell, Share2, Menu, X, BookOpen } from 'lucide-react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'; // Import NavLink
import './Sidebar.css';
import logo from '../logo.jpg';
import { useAuth } from '../../context/AuthContext';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || "student";
  const [isMobileView, setIsMobileView] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const roleLabel = {
    student: "Student",
    instructor: "Instructor",
    super_admin: "Super Admin",
  };
  const userInitial = (user?.fullName || "U").trim().charAt(0).toUpperCase();

  const baseItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/tracks", icon: FileText, label: "Tracks" },
    { to: "/leaderboard", icon: Trophy, label: "Leaderboard" },
  ];

  const roleItems = {
    student: [
      { to: "/student-guide", icon: BookOpen, label: "Documentation" },
      { to: "/tasks", icon: FileText, label: "Tasks" },
      { to: "/announcements", icon: Megaphone, label: "Announcements" },
      { to: "/assignment", icon: FileText, label: "Submit Task" },
      { to: "/feedback", icon: MessageSquare, label: "Feedback" },
    ],
    instructor: [
      { to: "/tasks", icon: FileText, label: "Tasks" },
      { to: "/create-announcement", icon: Megaphone, label: "Create Announcement" },
      { to: "/create-task", icon: FileText, label: "Create Task" },
      { to: "/track-social-preview", icon: Share2, label: "Social Share" },
      { to: "/tasks-submitted", icon: Users, label: "Submissions" },
      { to: "/grading-interface", icon: BarChart2, label: "Grading" },
      { to: "/notification-center", icon: Bell, label: "Notifications" },
      { to: "/enrollment-requests", icon: Users, label: "Enrollments" },
      { to: "/analytics-dashboard", icon: LineChart, label: "Analytics" },
    ],
    super_admin: [
      { to: "/tasks", icon: FileText, label: "Tasks" },
      { to: "/create-announcement", icon: Megaphone, label: "Create Announcement" },
      { to: "/create-task", icon: FileText, label: "Create Task" },
      { to: "/track-social-preview", icon: Share2, label: "Social Share" },
      { to: "/tasks-submitted", icon: Users, label: "Submissions" },
      { to: "/grading-interface", icon: BarChart2, label: "Grading" },
      { to: "/notification-center", icon: Bell, label: "Notifications" },
      { to: "/enrollment-requests", icon: Users, label: "Enrollments" },
      { to: "/analytics-dashboard", icon: LineChart, label: "Track Analytics" },
      { to: "/course-basics", icon: BookOpen, label: "Course Basics" },
      { to: "/course-settings", icon: Shield, label: "Course Settings" },
      { to: "/course-curriculum", icon: FileText, label: "Curriculum" },
      { to: "/course-preview", icon: Share2, label: "Course Preview" },
      { to: "/course-final", icon: Shield, label: "Course Final" },
      { to: "/super-admin", icon: Shield, label: "Control Panel" },
      { to: "/global-leaderboard", icon: Trophy, label: "Global Rank" },
    ],
  };

  const navItems = [...baseItems, ...(roleItems[role] || roleItems.student)];

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 1024px)");
    const applyMatch = () => setIsMobileView(mediaQuery.matches);
    applyMatch();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", applyMatch);
      return () => mediaQuery.removeEventListener("change", applyMatch);
    }

    mediaQuery.addListener(applyMatch);
    return () => mediaQuery.removeListener(applyMatch);
  }, []);

  React.useEffect(() => {
    if (!isMobileView) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobileView]);

  React.useEffect(() => {
    if (!isMobileView) return;
    setIsMobileMenuOpen(false);
  }, [isMobileView, location.pathname]);

  React.useEffect(() => {
    if (!isMobileView) {
      document.body.style.overflow = "";
      return undefined;
    }
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen, isMobileView]);

  const closeMobileMenuIfNeeded = () => {
    if (!isMobileView) return;
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    closeMobileMenuIfNeeded();
    await logout();
    navigate('/');
  };

  return (
    <>
      {isMobileView ? (
        <button
          type="button"
          className="sidebar-mobile-toggle"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      ) : null}

      {isMobileView && isMobileMenuOpen ? (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          className="sidebar-mobile-backdrop"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      ) : null}

      <div className={`sidebar ${isMobileView ? "sidebar-mobile" : ""} ${isMobileView && isMobileMenuOpen ? "open" : ""}`}>
      <div className="sidebar-header">
        <img src={logo} alt="IEEE Logo" className="sidebar-logo" />
        <h2 className="sidebar-brand">IEEE Study Groups</h2>
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeMobileMenuIfNeeded}
              className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}
            >
              <Icon size={20} className="nav-icon" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button type="button" onClick={handleLogout} className="nav-item exit-link border-0 bg-transparent text-start">
          <LogOut size={20} className="nav-icon" />
          <span>Exit</span>
        </button>
        
        <Link to='/profile' className="user-profile text-decoration-none" onClick={closeMobileMenuIfNeeded}>
          <div className="avatar">
            <span className="avatar-icon">{userInitial}</span>
            <div className="status-indicator"></div>
          </div>
          <div className="user-details">
            <h4 className="user-name">{user?.fullName || "User"}</h4>
            <p className="user-role">{roleLabel[role] || "User"}</p>
          </div>
        </Link>
      </div>
      </div>
    </>
  );
};

export default Sidebar;
